import { useState, useEffect, FormEvent, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setup2FA, verify2FA, ApiException } from '../api/client';
import { useAuth } from '../App';

/**
 * 2FA Setup Page
 *
 * QR Code + Manual Entry + Code Verification
 */
// Security: Nur interne Pfade erlauben (kein Open Redirect)
function getSafeReturnTo(returnTo: string | null): string {
  if (!returnTo) return '/dashboard';
  if (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.toLowerCase().startsWith('/http')) {
    return '/dashboard';
  }
  return returnTo;
}

export default function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  
  // returnTo aus URL Query Parameter
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setupCalled = useRef(false);

  useEffect(() => {
    if (setupCalled.current) return;
    setupCalled.current = true;
    loadSetup();
  }, []);

  const loadSetup = async () => {
    try {
      const data = await setup2FA();
      setQrCode(data.qrCodeDataUrl);
      setSecret(data.secret);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error);
      } else {
        setError('Fehler beim Laden der 2FA-Einrichtung');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setCode(pastedData.split(''));
    }
  };

  const handleVerify = async (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('');
    if (codeToVerify.length !== 6) return;

    setError('');
    setVerifying(true);

    try {
      await verify2FA(codeToVerify);
      // WICHTIG: AuthContext refreshen bevor Navigation, sonst ist user noch null
      await refresh();
      navigate(returnTo);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error || 'Ungültiger Code');
      } else {
        setError('Verbindungsfehler');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="kiosk-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="kiosk-container bg-gradient-to-br from-blue-50 to-gray-100">
      <div className="kiosk-card max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">2FA einrichten</h1>
          <p className="text-gray-500 mt-2">
            Scannen Sie den QR-Code mit Ihrer Authenticator-App
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* QR Code */}
        {qrCode && (
          <div className="flex justify-center mb-6">
            <img src={qrCode} alt="QR Code" className="w-48 h-48 border rounded-lg" />
          </div>
        )}

        {/* Manual Entry */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowManual(!showManual)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showManual ? 'QR-Code scannen' : 'Code manuell eingeben'}
          </button>

          {showManual && secret && (
            <div className="mt-3 p-3 bg-gray-100 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Geheimer Schlüssel:</p>
              <code className="text-sm font-mono break-all">{secret}</code>
            </div>
          )}
        </div>

        {/* Code Verification */}
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); handleVerify(); }}>
          <p className="text-sm text-gray-600 mb-3 text-center">
            Geben Sie den 6-stelligen Code ein:
          </p>

          <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                disabled={verifying}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={verifying || code.some((d) => !d)}
            className="btn btn-primary w-full"
          >
            {verifying ? 'Verifizieren...' : 'Aktivieren'}
          </button>
        </form>
      </div>
    </div>
  );
}
